import { useState } from "react";
import { FileText, Edit2, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import {
  useCandidateCertifications,
  useDeleteCertification,
} from "@/pages/candidate-profile/hooks/useCandidateProfile";
import { CertificationFormModal } from "@/pages/profile/employee/sections/components/CertificationFormModal";
import type { CertificationDetail } from "@/interface/certification";
import type { ProfileDetail } from "@/interface";

export function CertificationsSection({
  profileId,
}: {
  profileId?: number | string;
  profile?: ProfileDetail;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState<CertificationDetail | null>(
    null,
  );
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const {
    data: candidateCertRes,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateCertifications(profileId, { page: 1, page_size: 12 });
  const deleteMutation = useDeleteCertification();

  const certifications: CertificationDetail[] =
    candidateCertRes?.pages?.flatMap((page) =>
      Array.isArray(page?.data) ? page.data : page?.data?.results || [],
    ) || [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const handleOpenAdd = () => {
    setSelectedCert(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (cert: CertificationDetail) => {
    setSelectedCert(cert);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col max-h-[750px]">
      <div className="flex justify-between items-center mb-6 border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0">
        <div>
          <h2 className="text-lg font-bold text-primary">Certifications</h2>
          <p className="text-xs text-slate-500">
            Add professional certifications
          </p>
        </div>
        <Button
          onClick={handleOpenAdd}
          className="bg-[#1a0b2e] hover:bg-[#1a0b2e]/90 text-white text-xs h-8"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Certification
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto pr-1 flex-1 space-y-4 max-h-[500px]"
        >
          <div className="space-y-4">
            {certifications.map((cert: CertificationDetail) => {
              const certName = cert.certification_name || "";
              const issuerName = cert.issuing_organization || "";
              const issueDateVal = cert.issue_date || "";
              const expiryDateVal = cert.expiry_date || "";

              return (
                <div
                  key={cert.id}
                  className="flex justify-between items-center p-4 border border-slate-100 dark:border-slate-800 rounded-xl hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 bg-blue-500/10 rounded-lg flex items-center justify-center shrink-0">
                      <FileText className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-slate-900 dark:text-white">
                        {certName}
                      </h4>
                      <p className="text-xs text-slate-500">{issuerName}</p>
                      <div className="flex gap-4 mt-1 text-xs font-medium text-slate-400">
                        {issueDateVal && <span>Issued: {issueDateVal}</span>}
                        <span>Expires: {expiryDateVal || "No Expiration"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenEdit(cert)}
                      className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                    >
                      <Edit2 className="h-4 w-4 text-slate-400" />
                    </button>
                    <button
                      onClick={() => setDeleteTargetId(String(cert.id))}
                      className="p-2 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded transition-colors"
                    >
                      <Trash2 className="h-4 w-4 text-rose-400" />
                    </button>
                  </div>
                </div>
              );
            })}

            {certifications.length === 0 && (
              <div className="text-center p-8 text-slate-500 text-sm">
                No certifications added yet.
              </div>
            )}
          </div>

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-xs text-slate-500 font-medium">
                Loading more certifications...
              </span>
            </div>
          )}
        </div>
      )}

      <CertificationFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editData={selectedCert}
        profileId={profileId}
      />

      <ConfirmationDialog
        isOpen={Boolean(deleteTargetId)}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Certification"
        description="Are you sure you want to delete this certification?"
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
